import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  X,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  Sun,
  CalendarDays,
  CalendarRange,
  Sigma,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/**
 * Popup de métricas de uso da API /api/buscar_endereco.
 * Mostra contadores agregados (hoje, 7 dias, 30 dias, total) e gráficos
 * (requisições por dia e por hora de hoje). Os dados vêm de contadores
 * compactos no banco (1 documento por dia), então a consulta é leve e rápida.
 */
export default function ApiStatsModal({ api, onClose }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  // Aviso de filtro inválido (mostrado inline, SEM derrubar os dados da tela).
  const [erroFiltro, setErroFiltro] = useState("");
  // Filtro de período (vazio = últimos 30 dias, padrão do backend).
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [preset, setPreset] = useState("30d");
  const dadosRef = useRef(null);

  const carregar = useCallback(
    async (ini, f) => {
      setLoading(true);
      setErroFiltro("");
      try {
        const params = {};
        if (ini) params.inicio = ini;
        if (f) params.fim = f;
        const { data } = await axios.get(`${api}/metricas/buscar_endereco`, {
          params,
          timeout: 12000,
        });
        dadosRef.current = data;
        setDados(data);
        setErro(false);
      } catch (e) {
        const detalhe = e?.response?.data?.detail;
        if (dadosRef.current) {
          // Já há dados na tela: mantém tudo e avisa inline no filtro.
          setErroFiltro(
            detalhe || "Não foi possível aplicar o filtro. Tente novamente."
          );
        } else {
          setErro(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const fmtISO = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const fmtBR = (iso) =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";

  // Presets de período: 7/30/90 dias, este mês, mês passado.
  const aplicarPreset = (p) => {
    setPreset(p);
    const hoje = new Date();
    let ini = new Date(hoje);
    let f = hoje;
    if (p === "7d") ini.setDate(hoje.getDate() - 6);
    else if (p === "30d") ini.setDate(hoje.getDate() - 29);
    else if (p === "90d") ini.setDate(hoje.getDate() - 89);
    else if (p === "mes") ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    else if (p === "mes_passado") {
      ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      f = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    }
    const i = fmtISO(ini);
    const ff = fmtISO(f);
    setInicio(i);
    setFim(ff);
    carregar(i, ff);
  };

  const aplicarPersonalizado = () => {
    if (!inicio || !fim) {
      setErroFiltro("Preencha as duas datas para aplicar o filtro.");
      return;
    }
    if (inicio > fim) {
      setErroFiltro("A data inicial deve ser anterior ou igual à data final.");
      return;
    }
    const dias =
      Math.round((new Date(fim) - new Date(inicio)) / 86400000) + 1;
    if (dias > 366) {
      setErroFiltro(
        "Período máximo: 366 dias. Escolha um intervalo menor (dias sem registro aparecem como zero)."
      );
      return;
    }
    setErroFiltro("");
    setPreset("custom");
    carregar(inicio, fim);
  };

  const presets = [
    { id: "7d", rotulo: "7 dias" },
    { id: "30d", rotulo: "30 dias" },
    { id: "90d", rotulo: "90 dias" },
    { id: "mes", rotulo: "Este mês" },
    { id: "mes_passado", rotulo: "Mês passado" },
  ];

  // ESC fecha
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fmt = (n) => new Intl.NumberFormat("pt-BR").format(n ?? 0);

  const porDia = (dados?.por_dia || []).map((d) => ({
    ...d,
    rotulo: `${d.dia.slice(8, 10)}/${d.dia.slice(5, 7)}`,
  }));
  const porHora = (dados?.por_hora_hoje || []).map((h) => ({
    ...h,
    rotulo: `${h.hora}h`,
  }));

  const cards = [
    { titulo: "Hoje", valor: dados?.hoje, icone: Sun, cor: "text-teal-300", fundo: "bg-teal-400/15" },
    { titulo: "7 dias", valor: dados?.ultimos_7_dias, icone: CalendarDays, cor: "text-sky-300", fundo: "bg-sky-400/15" },
    { titulo: "30 dias", valor: dados?.ultimos_30_dias, icone: CalendarRange, cor: "text-violet-300", fundo: "bg-violet-400/15" },
    { titulo: "Total", valor: dados?.total, icone: Sigma, cor: "text-amber-300", fundo: "bg-amber-400/15" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8"
      data-testid="api-stats-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 240, damping: 26 }}
        className="glass relative w-full max-w-2xl rounded-2xl shadow-2xl shadow-black/60"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-teal-400/15 text-teal-300">
              <BarChart3 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate font-display text-base font-bold text-white">
                Métricas · Buscar Endereço
              </h2>
              <p className="truncate text-xs text-slate-400">
                Requisições a /api/buscar_endereco
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              onClick={() => carregar(inicio || undefined, fim || undefined)}
              disabled={loading}
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-300 transition-colors hover:bg-white/10 hover:text-teal-300 disabled:opacity-50"
              data-testid="api-stats-refresh-btn"
              aria-label="Atualizar métricas"
              title="Atualizar"
            >
              <RefreshCw className={`h-4.5 w-4.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-xl text-slate-300 transition-colors hover:bg-white/10 hover:text-rose-300"
              data-testid="api-stats-close-btn"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[78vh] space-y-5 overflow-y-auto px-5 py-5">
          {/* Skeleton loading (primeira carga) */}
          {loading && !dados && <SkeletonMetricas />}

          {/* Erro total (só quando ainda não há NENHUM dado na tela) */}
          {erro && !dados && !loading && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-300" strokeWidth={1.4} />
              <p className="text-sm text-slate-300">
                Não foi possível carregar as métricas.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => carregar()}
                  className="rounded-xl bg-teal-400 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-teal-300"
                >
                  Tentar novamente
                </button>
                <button
                  onClick={onClose}
                  className="rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/15"
                >
                  Fechar
                </button>
              </div>
            </div>
          )}

          {dados && (
            <>
              {/* Cards de resumo */}
              <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {cards.map((c) => (
                  <div
                    key={c.titulo}
                    className="rounded-xl border border-white/10 bg-black/25 p-3"
                    data-testid={`stat-card-${c.titulo}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`grid h-6 w-6 place-items-center rounded-lg ${c.fundo} ${c.cor}`}>
                        <c.icone className="h-3.5 w-3.5" />
                      </span>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        {c.titulo}
                      </p>
                    </div>
                    <p className={`mt-1.5 font-display text-2xl font-bold ${c.cor}`}>
                      {fmt(c.valor)}
                    </p>
                  </div>
                ))}
              </section>

              {/* Filtro de período */}
              <section data-testid="filtro-periodo">
                <h3 className="mb-2 text-sm font-bold text-white">Filtrar período</h3>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => aplicarPreset(p.id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                        preset === p.id
                          ? "bg-teal-400 text-slate-900"
                          : "bg-white/8 text-slate-300 hover:bg-white/15"
                      }`}
                      data-testid={`preset-${p.id}`}
                    >
                      {p.rotulo}
                    </button>
                  ))}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-slate-400">
                    De
                    <input
                      type="date"
                      value={inicio}
                      onChange={(e) => setInicio(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-teal-300/60"
                      data-testid="filtro-inicio"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-400">
                    até
                    <input
                      type="date"
                      value={fim}
                      onChange={(e) => setFim(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-teal-300/60"
                      data-testid="filtro-fim"
                    />
                  </label>
                  <button
                    onClick={aplicarPersonalizado}
                    disabled={loading}
                    className="rounded-lg bg-teal-400 px-3 py-1.5 text-xs font-semibold text-slate-900 transition-colors hover:bg-teal-300 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="filtro-aplicar-btn"
                  >
                    Aplicar
                  </button>
                </div>
                {/* Aviso de filtro inválido (inline — os dados continuam na tela) */}
                {erroFiltro && (
                  <p
                    className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-300/25 bg-amber-400/10 px-2.5 py-1.5 text-xs text-amber-200"
                    data-testid="filtro-erro"
                  >
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {erroFiltro}
                  </p>
                )}
              </section>

              {/* Gráfico: requisições por dia (período filtrado) */}
              <section>
                <h3 className="mb-1 text-sm font-bold text-white">
                  Requisições por dia
                </h3>
                {dados.periodo && (
                  <div
                    className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400"
                    data-testid="periodo-resumo"
                  >
                    <span>
                      Período:{" "}
                      <strong className="text-slate-200">
                        {fmtBR(dados.periodo.inicio)} – {fmtBR(dados.periodo.fim)}
                      </strong>{" "}
                      ({dados.periodo.dias}{" "}
                      {dados.periodo.dias === 1 ? "dia" : "dias"})
                    </span>
                    <span>
                      Total:{" "}
                      <strong className="text-teal-300">
                        {fmt(dados.periodo.total)}
                      </strong>
                    </span>
                    <span>
                      Média/dia:{" "}
                      <strong className="text-slate-200">
                        {new Intl.NumberFormat("pt-BR").format(
                          dados.periodo.media_por_dia ?? 0
                        )}
                      </strong>
                    </span>
                  </div>
                )}
                <div
                  className="rounded-xl border border-white/10 bg-black/25 p-2 pt-4"
                  data-testid="chart-por-dia"
                >
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={porDia} margin={{ top: 0, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
                      <XAxis
                        dataKey="rotulo"
                        tick={{ fill: "#94a3b8", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={24}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "#94a3b8", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        width={38}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(45,212,191,0.08)" }}
                        contentStyle={{
                          background: "rgba(4,10,20,0.95)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 12,
                          color: "#e2e8f0",
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#94a3b8" }}
                        formatter={(v) => [fmt(v), "requisições"]}
                      />
                      <Bar dataKey="total" fill="#2dd4bf" radius={[3, 3, 0, 0]} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>

              {/* Gráfico: hoje por hora */}
              <section>
                <h3 className="mb-2 text-sm font-bold text-white">
                  Hoje por hora{" "}
                  <span className="font-normal text-slate-400">
                    · fuso {dados.fuso_horario || "America/Sao_Paulo"}
                  </span>
                </h3>
                <div
                  className="rounded-xl border border-white/10 bg-black/25 p-2 pt-4"
                  data-testid="chart-por-hora"
                >
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={porHora} margin={{ top: 0, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
                      <XAxis
                        dataKey="rotulo"
                        tick={{ fill: "#94a3b8", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={18}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "#94a3b8", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        width={38}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(56,189,248,0.08)" }}
                        contentStyle={{
                          background: "rgba(4,10,20,0.95)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 12,
                          color: "#e2e8f0",
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#94a3b8" }}
                        formatter={(v) => [fmt(v), "requisições"]}
                      />
                      <Bar dataKey="total" fill="#38bdf8" radius={[3, 3, 0, 0]} maxBarSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>

              {/* Rodapé: comparação com ontem + atualização */}
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                <span>
                  Ontem: <strong className="text-slate-200">{fmt(dados.ontem)}</strong>{" "}
                  requisições
                </span>
                {dados.atualizado_em && (
                  <span>
                    Atualizado às{" "}
                    {new Date(dados.atualizado_em).toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Alturas fixas das barras do skeleton (visual de gráfico "fantasma").
const BARRAS_SKELETON = [35, 55, 25, 70, 45, 60, 30, 80, 50, 65, 40, 75, 55, 45, 68, 38, 58, 48, 72, 42];

/** Skeleton de carregamento do popup de métricas (primeira carga). */
function SkeletonMetricas() {
  return (
    <div className="animate-pulse space-y-5" data-testid="stats-skeleton" aria-busy="true">
      {/* Cards */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-1.5">
              <div className="h-6 w-6 rounded-lg bg-white/10" />
              <div className="h-3 w-14 rounded bg-white/10" />
            </div>
            <div className="mt-2.5 h-7 w-16 rounded bg-white/10" />
          </div>
        ))}
      </div>

      {/* Filtro */}
      <div>
        <div className="h-4 w-28 rounded bg-white/10" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-7 w-20 rounded-full bg-white/10" />
          ))}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-2">
          <div className="h-8 w-36 rounded-lg bg-white/10" />
          <div className="h-8 w-36 rounded-lg bg-white/10" />
          <div className="h-8 w-16 rounded-lg bg-white/10" />
        </div>
      </div>

      {/* Gráfico diário */}
      <div>
        <div className="h-4 w-44 rounded bg-white/10" />
        <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="flex h-[150px] items-end gap-1.5">
            {BARRAS_SKELETON.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t bg-white/10"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Gráfico horário */}
      <div>
        <div className="h-4 w-36 rounded bg-white/10" />
        <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="flex h-[110px] items-end gap-1">
            {BARRAS_SKELETON.concat([52, 62, 33, 47]).map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t bg-white/10"
                style={{ height: `${(h * 0.8) | 0}%` }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
